import { DataTableSelectedData, PaginationRequest, PaginationResponse } from '@repo/ui/data-table'
import { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { restApiClient } from '../../../api/rest-api-client'
import { assignMissionToTripFolder } from '../../../api/trip-folder/assign-mission-to-trip-folder'
import { Mission } from '../../../lib/types/models/mission/mission'
import { MissionFilter } from '../../../lib/types/models/mission/mission-filter'
import { encodeNestedObject, showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { queryClient } from '../../../main'
import { missionColumns } from '../../../views/missions/mission-list'
import ActionButtons from '../../elements/action-buttons'
import DynamicDataTable from '../../elements/dynamic-table'

interface TripFolderAddMissionProps {
  tripFolderId: number
  toggleModal: () => void
}

const TripFolderAddMission = ({ tripFolderId, toggleModal }: TripFolderAddMissionProps) => {
  const missionFilters: MissionFilter = { excludeTripFolderId: tripFolderId }
  const [selectedMissions, setSelectedMissions] = useState<DataTableSelectedData<Mission>>({
    isAllSelected: false,
    rows: [],
    expandedRows: []
  })
  const [search, setSearch] = useState('')

  const { mutate: assignMissions, isPending } = useMutation({
    onError: showAxiosError,
    mutationFn: async () => {
      const missionIds = selectedMissions.isAllSelected
        ? (
            await restApiClient
              .url(
                encodeNestedObject<PaginationRequest<MissionFilter>>(API.missions.getAll.url, {
                  filter: missionFilters,
                  search,
                  version: 'ID_ONLY'
                })
              )
              .get<PaginationResponse<Pick<Mission, 'id'>>>()
          ).data.map((mission) => mission.id)
        : selectedMissions.rows.map((mission) => mission.id)

      return assignMissionToTripFolder(tripFolderId, {
        missionIds
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trip-folder-missions', tripFolderId] })
      queryClient.invalidateQueries({ queryKey: [API.missions.getAll.url] })
      toast.success('Missions assignées avec succès')
      toggleModal()
    }
  })

  return (
    <div className={MODAL_BODY_CLASSNAME}>
      <DynamicDataTable<Mission, MissionFilter>
        title={'Toutes les missions'}
        columns={missionColumns()}
        deleteMessage={{ name: 'la mission', attributeName: 'title' }}
        selectable
        allowSearchParam
        filters={missionFilters}
        selectedData={selectedMissions}
        onSearchChange={setSearch}
        onSelectedDataChange={setSelectedMissions}
        paginated={false}
        headerType="light"
        endpoint={API.missions.getAll.url}
        permissions={{ delete: false }}
      />

      <ActionButtons isLoading={isPending} onSubmit={assignMissions} onCancel={toggleModal} />
    </div>
  )
}

export default TripFolderAddMission
